import React, { useMemo, useState } from 'react';
import { Row, Col, Form, Table, Spinner, Alert } from 'react-bootstrap';
import { BuyButton } from '../../components/BuyButtons';
import PredictionChart from '../../components/PredictionChart';
import { fetchListingLookup } from '../../services/Api';
import './PropertyCatalog.css';

const formatGBP = (n) =>
    n == null ? '-' : `£${Math.round(n).toLocaleString('en-GB')}`;

const formatGBPBin = (n, bin = 25000) => {
    if (n == null) return '-';
    const lower = Math.floor(n / bin) * bin;
    const fmt = (v) => v >= 1_000_000
        ? `£${(v / 1_000_000).toLocaleString('en-GB', { maximumFractionDigits: 3 })}m`
        : `£${(v / 1000).toLocaleString('en-GB')}k`;
    return `between ${fmt(lower)} and ${fmt(lower + bin)}`;
};

const isSupportedUrl = (url) =>
    /^https?:\/\/(www\.)?rightmove\.co\.uk\//i.test(url.trim());

const COMPARABLE_COLUMNS = [
    { key: 'date',            label: 'Date',     numeric: false, align: 'start' },
    { key: 'address',         label: 'Address',  numeric: false, align: 'start' },
    { key: 'type',            label: 'Type',     numeric: false, align: 'start' },
    { key: 'price',           label: 'Price',    numeric: true,  align: 'end'   },
    { key: 'distance_metres', label: 'Distance', numeric: true,  align: 'end'   },
];

// Dev-only mock so the layout can be iterated without burning real lookups.
// Gated on NODE_ENV so webpack/terser drops the entire object literal in
// production builds: `process.env.NODE_ENV` becomes the string 'production'
// at build time, the ternary collapses to `null`, and the dead object goes.
const MOCK_RESULT = process.env.NODE_ENV === 'development' ? {
    success: true,
    paywalled: false,
    listing: {
        listing_id: '999999999',
        source: 'rightmove',
        postcode_area: 'SN15',
        property_type: 'Detached',
        total_floor_area: 145,
        tenure: 'Freehold',
        new_build: false,
    },
    valuation: (() => {
        // 100 estimates roughly normal-ish around £620k
        const estimates = Array.from({ length: 100 }, () =>
            620000 + (Math.random() - 0.5) * 180000
        );
        const sorted = [...estimates].sort((a, b) => a - b);
        return {
            mean: estimates.reduce((s, n) => s + n, 0) / estimates.length,
            median: sorted[50],
            low: sorted[10],
            high: sorted[90],
            estimates,
        };
    })(),
    comparables: [
        ['2024-09-04', '87 Cresswell Close, Yarnton',          'Terraced',     311000, 83],
        ['2024-03-15', '9 Briar Thicket',                       'Detached',     645000, 142],
        ['2024-01-12', 'Flat 4 Erdington House',                'Flat',         195000, 6],
        ['2023-10-04', '23 Cassington Road, Yarnton',           'Semi-detached', 323000, 66],
        ['2023-08-21', '14 Marlborough Avenue',                 'Detached',     720000, 198],
        ['2023-06-30', '2 The Old Coach House',                 'Detached',     895000, 240],
        ['2023-04-11', '7 Briar Thicket',                       'Detached',     612000, 138],
        ['2022-11-09', '11 Cresswell Close',                    'Terraced',     290000, 88],
        ['2022-04-14', 'Flat 8 Erdington House',                'Flat',         187500, 7],
        ['2025-04-16', '22 Plane Tree Way',                     'Terraced',     345000, 305],
    ].map(([date, address, type, price, distance_metres]) =>
        ({ date, address, type, price, distance_metres })
    ),
    message: 'mock',
} : null;

/**
 * Body-only "valuation by URL" panel. Renders no SEO, no card chrome, no
 * auth gate - the parent (Predictor) owns those. Caller passes the
 * authenticated `user` so we can pull the email for the lookup + BuyButton.
 */
function PropertyCatalog({ user }) {
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [sort, setSort] = useState({ key: 'distance_metres', dir: 'asc' });
    const [filter, setFilter] = useState('');

    const toggleSort = (key) => {
        setSort((s) => s.key === key
            ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: COMPARABLE_COLUMNS.find(c => c.key === key)?.numeric ? 'desc' : 'asc' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setResult(null);

        if (!isSupportedUrl(url)) {
            setError('Please paste a Rightmove listing URL.');
            return;
        }

        setIsLoading(true);
        try {
            const data = await fetchListingLookup({
                email: user.signInDetails.loginId.toLowerCase(),
                url: url.trim(),
            });

            if (data?.paywalled) {
                setResult(data);
            } else if (!data || !data.success) {
                setError(data?.message || 'Could not extract details from that listing.');
            } else {
                setResult(data);
            }
        } catch (err) {
            // Avoid logging the full error object - it can contain the partial
            // GraphQL response body, which leaks Rightmove-derived data into
            // the browser console.
            console.error('Listing lookup failed:', err?.message || 'unknown error');
            setError(`Lookup failed: ${err.message || 'unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const listing = result?.listing;
    const valuation = result?.valuation;
    const totalComparables = result?.comparables?.length ?? 0;

    const estimateChartData = useMemo(() => {
        const estimates = result?.valuation?.estimates;
        if (!estimates || estimates.length === 0) return null;
        return estimates.map(value => ({ value }));
    }, [result]);

    const sortedComparables = useMemo(() => {
        const rows = result?.comparables;
        if (!rows || rows.length === 0) return [];

        const q = filter.trim().toLowerCase();
        const filtered = q
            ? rows.filter(r =>
                String(r.address ?? '').toLowerCase().includes(q)
                || String(r.type ?? '').toLowerCase().includes(q)
                || String(r.date ?? '').toLowerCase().includes(q))
            : rows;

        const col = COMPARABLE_COLUMNS.find(c => c.key === sort.key);
        const sign = sort.dir === 'asc' ? 1 : -1;
        const cmp = col?.numeric
            ? (a, b) => sign * ((a[sort.key] ?? Infinity) - (b[sort.key] ?? Infinity))
            : (a, b) => sign * String(a[sort.key] ?? '').localeCompare(String(b[sort.key] ?? ''));
        return [...filtered].sort(cmp);
    }, [result, sort, filter]);

    return (
        <div className="catalog-body">
            {/* Row 1: input + inputs panel (left) | model outcome (right) */}
            <Row>
                <Col md={6} xs={12} className="catalog-input-col">
                    <p className="catalog-intro">
                        Paste a <strong>Rightmove</strong> listing URL. We&apos;ll extract the
                        postcode, property type and floor area, then run our machine-learning
                        fair-value model and surface recent comparable sales nearby.
                    </p>
                    <p className="catalog-intro">
                        <em>Free accounts get 2 listing lookups. Subscribe for unlimited valuations and comparables.</em>
                    </p>

                    <Form onSubmit={handleSubmit} className="catalog-form">
                        <Form.Group controlId="listingUrl">
                            <Form.Label>Listing URL</Form.Label>
                            <div className="catalog-input-row">
                                <Form.Control
                                    type="url"
                                    placeholder="https://www.rightmove.co.uk/properties/12345678"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    disabled={isLoading}
                                    required
                                />
                                <button
                                    type="submit"
                                    className="otta-button"
                                    disabled={isLoading || !url}
                                >
                                    {isLoading ? <><Spinner size="sm" animation="border" /> Looking up…</> : 'Look up'}
                                </button>
                            </div>
                        </Form.Group>
                    </Form>

                    {process.env.NODE_ENV === 'development' && (
                        <div className="catalog-mock-row">
                            <button
                                type="button"
                                className="catalog-mock-button"
                                onClick={() => { setError(''); setResult(MOCK_RESULT); }}
                            >
                                Load mock data (dev only)
                            </button>
                        </div>
                    )}

                    {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

                    {isLoading && <div className="catalog-loading">Looking up...</div>}

                    {result?.paywalled && (
                        <div className="catalog-paywall">
                            <h3>You&apos;ve used your free lookups</h3>
                            <p>{result.message || 'Subscribe for unlimited valuations and comparables.'}</p>
                            <BuyButton userEmail={user.signInDetails.loginId.toLowerCase()} />
                        </div>
                    )}

                    {result && listing && (
                        <div className="catalog-panel mt-3">
                            <h3>Inputs used</h3>
                            <p className="catalog-inputs-note">
                                Listing <strong>{listing.listing_id || '-'}</strong> from {listing.source || 'unknown'}. Model inputs we derived:
                            </p>
                            <Table size="sm" borderless className="catalog-kv">
                                <tbody>
                                    <tr><th>Postcode area</th><td>{listing.postcode_area || '-'}</td></tr>
                                    <tr><th>Property type</th><td>{listing.property_type || '-'}</td></tr>
                                    <tr><th>Floor area</th><td>{listing.total_floor_area ? `${Math.round(listing.total_floor_area)} m²` : '-'}</td></tr>
                                    <tr><th>Tenure</th><td>{listing.tenure || '-'}</td></tr>
                                    <tr><th>New build</th><td>{listing.new_build ? 'Yes' : 'No'}</td></tr>
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Col>

                <Col md={6} xs={12} className="catalog-results-col">
                    {result && listing && (
                        <div className="catalog-panel">
                            <h3>What the model thinks</h3>
                            {valuation ? (
                                <>
                                    <p className="catalog-model-note">
                                        We ran the inputs through {valuation.estimates?.length || 100} different
                                        models. The <strong>shape</strong> of the chart matters more than
                                        any single number - a narrow peak means the models agree, a wide
                                        or messy spread means they don&apos;t.
                                    </p>
                                    <div className="catalog-valuation">
                                        <div className="catalog-big-number">{formatGBPBin(valuation.median)}</div>
                                        <div className="catalog-range">
                                            10th-90th percentile: {formatGBP(valuation.low)} - {formatGBP(valuation.high)}
                                        </div>
                                    </div>
                                    {estimateChartData && (
                                        <div className="catalog-chart-wrap">
                                            <PredictionChart data={estimateChartData} />
                                        </div>
                                    )}
                                    <details className="catalog-caveats">
                                        <summary>Model caveats</summary>
                                        <p>
                                            Trained mostly on typical UK homes around 70-150 m². For
                                            larger places, period builds, unusual plots or anywhere
                                            with location scarcity, the prediction degrades fast -
                                            for properties over ~200 m² in particular, expect to be wrong.
                                        </p>
                                    </details>
                                </>
                            ) : (
                                <p>The model couldn&apos;t produce a prediction for this listing.</p>
                            )}
                        </div>
                    )}
                </Col>
            </Row>

            {/* Row 2: full-width comparable recent sales */}
            {result && listing && (
                <div className="catalog-comparables-section mt-3">
                    <h4 className="catalog-subheading">Comparable recent sales</h4>
                    {totalComparables > 0 && (
                        <div className="catalog-filter-row">
                            <Form.Control
                                size="sm"
                                type="search"
                                placeholder="Filter by address, type, or date…"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="catalog-filter-input"
                            />
                            <span className="catalog-filter-count">
                                {filter.trim()
                                    ? `${sortedComparables.length} of ${totalComparables}`
                                    : `${totalComparables} sale${totalComparables === 1 ? '' : 's'}`}
                            </span>
                        </div>
                    )}
                    {totalComparables === 0 ? (
                        <p>No comparable sales found within 2km.</p>
                    ) : sortedComparables.length === 0 ? (
                        <p>No matches for &ldquo;{filter}&rdquo;.</p>
                    ) : (
                        <div className="table-responsive catalog-comparables-wrap">
                            <Table striped bordered hover size="sm" className="catalog-comparables">
                                <thead>
                                    <tr>
                                        {COMPARABLE_COLUMNS.map(col => {
                                            const isActive = sort.key === col.key;
                                            const arrow = isActive ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
                                            return (
                                                <th
                                                    key={col.key}
                                                    onClick={() => toggleSort(col.key)}
                                                    className={`sortable text-${col.align}${isActive ? ' active' : ''}`}
                                                >
                                                    {col.label}{arrow}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedComparables.slice(0, 25).map((c, i) => (
                                        <tr key={i}>
                                            <td>{c.date}</td>
                                            <td>{c.address}</td>
                                            <td>{c.type}</td>
                                            <td className="text-end">{formatGBP(c.price)}</td>
                                            <td className="text-end">{c.distance_metres ? `${Math.round(c.distance_metres)}m` : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default PropertyCatalog;
